import { NoteStructureType } from '@/components/ui/choose-note-structure-modal/choose-note-structure-command/types';
import { trackSignupConversion } from '@/lib/analytics/trackSignupConversion';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { store } from '@/store';
import { logger } from '@/utils/logger/logger';
import {
  extractKindeUserIdFromAccessToken,
  extractKindeUserIdFromAuth0Token,
} from '@heidi/auth/tokens';
import { TemplateCategory } from '@heidi/schema/ml-scribe';
import { type TemplateResponse } from '@heidi/templates/contracts';
import { getWorkspaceId, setWorkspaceId } from '@heidi/workspace/data';
import { differenceInCalendarDays } from 'date-fns';
import { type IMediaRecorder } from 'extendable-media-recorder';

/* IMPORTANT
These imports used rematch model, so we need to import them here to avoid reference errors.
*/
import { trackMediaRecorderUnableToStop } from '@/lib/analytics/scribe/general';
import { getUser as getUserFromAPI } from '@/pages/api/scribeAPI';

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
export const websocketSocketIntentionalCloseCodes = [4002, 4003, 4004];
export const websocketBECloseCodes = [
  4000,
  4001,
  ...websocketSocketIntentionalCloseCodes,
];

export const INTERNAL_SERVER_ERROR = 4000;
// 4001: WS received a different payload than expected
export const UNSUPPORTED_PAYLOAD_ERROR = 4001;
// 4002: a user is accessing a soft deleted resource
export const RESOURCE_DELETED_ERROR = 4002;
// 4003: resource not found in DB
export const RESOURCE_NOT_FOUND_ERROR = 4003;
// 4004: a user is accessing someone else resource or permission denied
export const UNAUTHORIZED = 4004;

export const intentionalCloseCodesMapping: Record<number, string> = {
  [INTERNAL_SERVER_ERROR]: 'Internal Server Error',
  [UNSUPPORTED_PAYLOAD_ERROR]: 'Unsupported Payload Error',
  [RESOURCE_DELETED_ERROR]: 'Your session has been deleted. Please create a new session',
  [RESOURCE_NOT_FOUND_ERROR]: 'Session not found. Please create a new sesion',
  [UNAUTHORIZED]:
    'You are not authorized to access this session. Please create a new session',
};

// Promoted with its cases, beside the precedent below.
export { parseCapitalizeString } from '@heidi/utils';

// Definition promoted to `@heidi/utils` (D5 documents, ARCFE-990).
export { normalizedStringCompare } from '@heidi/utils';

export const stopMediaRecorder = (mediaRecorder: MediaRecorder | IMediaRecorder) => {
  try {
    if (mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  } catch (e) {
    trackMediaRecorderUnableToStop();
    logger.error('error stopping media recorder', e);
  }
};

export function calculateTimeDeltaSeconds(start: string, end: string): number {
  // returns the time delta in seconds
  const startDate = start ? new Date(start) : new Date();
  const endDate = end ? new Date(end) : new Date();
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
  return (endDate.getTime() - startDate.getTime()) / 1000;
}

export function calculateTrialDaysLeft(
  isInFreeTrial: boolean,
  trialEnd: string | undefined | null,
  maxDays?: number
): number {
  if (!isInFreeTrial || !trialEnd) {
    return 1;
  }

  const trialEndDate = parseTimeStamp(trialEnd);
  const daysLeft = differenceInCalendarDays(trialEndDate, new Date());

  return maxDays != null
    ? Math.min(maxDays, Math.max(1, daysLeft))
    : Math.max(1, daysLeft);
}

export function calculateTrialHoursLeft(
  isInFreeTrial: boolean,
  trialEnd: string | undefined | null
): number {
  if (!isInFreeTrial || !trialEnd) {
    return 0;
  }

  const trialEndDate = new Date(trialEnd);

  if (isNaN(trialEndDate.getTime())) {
    return 1;
  }

  const currentTimeUTC = Date.now();
  const hoursLeft = Math.ceil(
    (trialEndDate.getTime() - currentTimeUTC) / (1000 * 60 * 60)
  );

  return Math.max(1, hoursLeft);
}

/**
 * Validates if the provided value is a valid EHR patient ID. Currently used for Medical Director integration.
 * @param patientId - The patient ID to validate
 * @returns true if the patient ID is defined and can be converted to a valid number
 */
export const isValidEHRPatientId = (patientId: string | number | undefined) =>
  !!(patientId && !Number.isNaN(Number(patientId)));

export const getProductImage = () =>
  'bg-[url("/assets/images/product-blur-bg-light.webp")] newBrandDark:bg-[url("/assets/images/product-blur-bg-dark.webp")] bg-cover bg-top';

export const isProTemplate = (
  template: TemplateResponse | null | undefined,
  noteStructureType: NoteStructureType
) => {
  if (!template) return false;
  if (!template.should_deduct_quota) return false;
  return (
    template.is_private ||
    template.template_category === TemplateCategory.DOCUMENT_TEMPLATE ||
    noteStructureType === NoteStructureType.Document
  );
};

export const getUser = async () => {
  const result = await getUserFromAPI();

  if (typeof window === 'undefined') {
    return result;
  }

  // Cast until the schema is regenerated against the paired ml-scribe change.
  const isNewlyCreated = (result?.data as { is_newly_created?: boolean } | undefined)
    ?.is_newly_created;

  // `GET /workspaces` 400s until the user row exists, so boot hydration leaves
  // the id null and the profile PATCHes that follow carry no workspace header.
  // The token check rejects a response landing after logout, where `auth.clear()`
  // has run but this continuation would still see the freshly nulled atom.
  const provisionedWorkspaceId = result?.data?.workspace_id;
  if (
    isNewlyCreated &&
    provisionedWorkspaceId &&
    getWorkspaceId() === null &&
    store.getState().auth?.token
  ) {
    setWorkspaceId(provisionedWorkspaceId);
  }

  // Browser-side half of the Meta signup-conversion pair (server half is the
  // backend `sign_up_successful`). `getUser` is the single choke point every
  // get-or-create entry point funnels through — the app-boot context query,
  // the onboarding-v2 completion `fetchQuery`, dictation about-you, etc. —
  // so firing here (rather than in one caller) guarantees the load-bearing
  // event is emitted for every signup path, exactly once: the backend returns
  // `is_newly_created: true` only on the request that creates the row. The
  // dispatch itself is flag-gated inside `trackSignupConversion`.
  if (isNewlyCreated) {
    // Meta dedup `event_id` = `kinde_user_id`, derived from the access token
    // rather than the response body. The token is always present post-auth (the
    // response field can lag for a brand-new user), and the backend
    // `sign_up_successful` reads the SAME token claim — so FE and BE emit an
    // identical `event_id` and Meta can dedupe the pair. Provider-agnostic:
    // Kinde carries it top-level, Auth0 under `user_properties.kinde_user_id.v`.
    const token = store.getState().auth?.token;
    const kindeUserId = token
      ? (extractKindeUserIdFromAccessToken(token) ??
        extractKindeUserIdFromAuth0Token(token))
      : undefined;
    trackSignupConversion(kindeUserId);
  }

  return result;
};
